import { CategoryInterface, ProductInterface } from '@graphcommerce/graphql-mesh'
import { JsonLd, filterNonNullableKeys } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import Link from 'next/link'
import React, { useMemo } from 'react'
import { jsonLdBreadcrumbs } from '../Category/JsonLdCategory/jsonLdBreadcrumbs'
import { Icon } from '../Layout/Icon'

export type BreadcrumbsProps = {
  product?: Pick<ProductInterface, 'uid' | 'name' | 'url_path' | 'categories' | 'url_key'>
  category?: Pick<CategoryInterface, 'uid' | 'name' | 'url_path' | 'breadcrumbs'>
}

const baseUrl = import.meta.graphCommerce.canonicalBaseUrl

export function Breadcrumbs(props: BreadcrumbsProps) {
  const { product, category } = props

  const crumbs = useMemo(() => {
    const result = [
      { key: 'home', label: i18n._(/* i18n */ 'Home'), link: '/', url: new URL(baseUrl) },
    ] as {
      key: string
      label: string
      link: string | null
      url: URL
    }[]

    if (product) {
      const mainCategory = product.categories?.length ? product.categories[0] : false
      if (mainCategory) {
        result.push({
          key: mainCategory.uid,
          label: mainCategory.name || '',
          link: `/${mainCategory.url_path}` || '',
          url: new URL(`${baseUrl}${mainCategory.url_path}`),
        })
      }
      result.push({
        key: product.uid,
        label: product.name || '',
        link: product.url_path || '',
        url: new URL(`${baseUrl}p/${product.url_key}`),
      })
    } else if (category) {
      filterNonNullableKeys(category.breadcrumbs, ['category_level'])
        .sort((a, b) => a.category_level - b.category_level)
        .forEach((breadcrumb) => {
          result.push({
            key: breadcrumb.category_uid,
            label: breadcrumb.category_name || '',
            link: `/${breadcrumb.category_url_path}`,
            url: new URL(`${baseUrl}${breadcrumb.category_url_path}`),
          })
        })

      result.push({
        key: category.uid,
        label: category.name || '',
        link: null,
        url: new URL(`${baseUrl}${category.url_path}`),
      })
    }
    return result
  }, [product, category])

  if (!crumbs.length) {
    return null
  }

  return (
    <div className='Type-Large-Regular flex items-center bg-lightest-gray py-[6px] text-70-grey '>
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          ...jsonLdBreadcrumbs({ crumbs }),
        }}
      />
      {crumbs?.map((crumb, index) => (
        <React.Fragment key={crumb.key}>
          {crumb.link ? (
            <Link
              href={`${crumb.link}`}
              className={index === crumbs.length - 1 ? 'Type-Large-Bold truncate' : ''}
            >
              {crumb.label}
            </Link>
          ) : (
            <div className={index === crumbs.length - 1 ? 'Type-Large-Bold truncate' : ''}>
              {crumb.label}
            </div>
          )}
          {index !== crumbs.length - 1 && <Icon name='chevron_right' />}
        </React.Fragment>
      ))}
    </div>
  )
}
